import { createRootRouteWithContext, Outlet, Link } from "@tanstack/react-router"
import type { QueryClient } from "@tanstack/react-query"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { PageLoader } from "@/components/ui/page-loader"
import { PageError } from "@/components/ui/page-error"
import { captureError } from "@/lib/telemetry/sentry"
import { MapPin } from "lucide-react"
import { Button } from "@/components/ui/button"

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  pendingComponent: () => <PageLoader message="Loading application..." />,
  errorComponent: ({ error }) => {
    captureError(error)
    return <PageError error={error as Error} />
  },
  notFoundComponent: NotFound,
})

function RootLayout() {
  return (
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  )
}

function NotFound() {
  return (
    <div className="flex h-screen items-center justify-center bg-[var(--color-bg-primary)]">
      <div className="w-full max-w-md rounded-[var(--radius-panel)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-secondary)] p-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-bg-tertiary)]">
            <MapPin className="h-7 w-7 text-[var(--color-text-muted)]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
              Page not found
            </h1>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              The page you are looking for does not exist or has been moved.
            </p>
          </div>
          <Button asChild variant="default" size="default">
            <Link to="/">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}
